import React, {
  useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Row, Col } from 'antd';
import PropTypes from 'prop-types';

import CompanySettingsCard from '../CompanySettingsCard';
import HoverHelp from '../../common/HoverHelp';
import UserAssignmentSelector from '../../forms/FormWorkflows/selectors/UserAssignmentSelector';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';

const tokenSeparators = [',', '\n'];

const emailRegex = new RegExp(/^[^@\s]+@[^@\s\.]+(?:\.[^@\.\s]+)+$/);

const formatBatchEmail = (batchEmail) => ({
  subject: batchEmail?.subject ?? '',
  body: batchEmail?.body ?? '',
  recipients: batchEmail?.recipients ?? [],
});

export default function ABWorkReportingBatchEmailSettings({
  batchEmail,
  saveCompanySettings,
  disabled = false,
}) {
  const formattedBatchEmail = formatBatchEmail(batchEmail);
  const [local, setLocal] = useState(() => formattedBatchEmail);
  const [selectedRecipients, setSelectedRecipients] = useState(
    () => formattedBatchEmail.recipients,
  );

  const users = useSelector((state) => state.users.users);
  const positionNames = useSelector((state) => state.settings.positionNames);

  const userIds = useMemo(() => {
    const idSet = new Set();
    users.forEach((user) => {
      if (user.active) {
        idSet.add(user.id);
      }
    });
    return idSet;
  }, [users]);

  const positionIds = useMemo(() => {
    const idSet = new Set();
    positionNames.forEach((position) => {
      idSet.add(position.id);
    });
    return idSet;
  }, [positionNames]);

  const validate = (selectedValues) => selectedValues.filter((emailOrId) => (
    userIds.has(emailOrId)
    || positionIds.has(emailOrId)
    || emailRegex.test(emailOrId)
  ));

  useEffect(() => {
    const next = formatBatchEmail(batchEmail);
    setLocal(next);
    setSelectedRecipients(next.recipients);
  }, [batchEmail]);

  const updateSettings = (field) => (newValue) => {
    const formattedValue = newValue?.target?.value ?? newValue;
    setLocal((prev) => {
      const next = { ...prev, [field]: formattedValue };
      saveCompanySettings({ abWorkReportingBatchEmail: next });
      return next;
    });
  };

  const onRecipientsChange = (newSelected) => {
    const filtered = validate(newSelected);
    setSelectedRecipients(filtered);
    updateSettings('recipients')(filtered);
  };

  const onTextInputChange = (field) => (e) => {
    const value = e?.target?.value ?? '';
    setLocal((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <CompanySettingsCard
      title="Batch export email"
      leftHeaderView={(
        <HoverHelp
          content="When a crew worksheet batch is generated, an email is sent to the configured recipients with the subject and body below. The batch text file is attached. Use {{batchNumber}} in the subject or body to insert the batch number."
          placement="top"
          iconProps={{ style: { fontSize: 16 } }}
        />
      )}
    >
      <Row style={{ marginBottom: 10, marginTop: 10 }}>
        <Col span={6}>Email subject</Col>
        <Col span={18}>
          <OnTraccrTextInput
            value={local.subject}
            disabled={disabled}
            placeholder="e.g. AB batch export {{batchNumber}}"
            onChange={onTextInputChange('subject')}
            onBlur={updateSettings('subject')}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={6}>Email body</Col>
        <Col span={18}>
          <OnTraccrTextInput
            textarea
            value={local.body}
            disabled={disabled}
            rows={4}
            placeholder="Message shown in the email (plain text)."
            onChange={onTextInputChange('body')}
            onBlur={updateSettings('body')}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 10 }}>
        <Col span={24}>
          <UserAssignmentSelector
            onChange={onRecipientsChange}
            selected={selectedRecipients}
            mode="tags"
            showPositions
            type="email"
            text="Select batch export recipient(s):"
            tokenSeparators={tokenSeparators}
            disabled={disabled}
          />
        </Col>
      </Row>
    </CompanySettingsCard>
  );
}

ABWorkReportingBatchEmailSettings.propTypes = {
  batchEmail: PropTypes.shape({
    subject: PropTypes.string,
    body: PropTypes.string,
    recipients: PropTypes.arrayOf(PropTypes.string),
  }),
  saveCompanySettings: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ABWorkReportingBatchEmailSettings.defaultProps = {
  batchEmail: {},
  disabled: false,
};
